import { useCallback, useMemo } from "react";

import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Chain, ChainOrAll } from "database";
import { slice } from "utils/string";
import NameEdit from "./NameEdit";
import useContact from "hooks/useContact";
import { trpc } from "@utils";
import { useNotification } from "hooks/useNotification";
import Image from "next/image";
import { PopupCard } from "@ui";

interface ContactProps {
    walletAddress: string;
    placeholder?: string;
    showAddress?: boolean;
    chain: ChainOrAll | Chain;
    showRequestIcon?: boolean;
}

const Contact = ({
    walletAddress,
    placeholder = "Name",
    showAddress,
    chain,
    showRequestIcon = false,
}: ContactProps) => {
    const { contact, status, isLoading } = useContact({ walletAddress, chain });

    const notify = useNotification();

    const isContactError = useMemo(() => status === "error", [status]);

    const name = useMemo(() => {
        if (contact) {
            return contact.name;
        }

        if (showAddress) {
            return slice(walletAddress, 7);
        }
    }, [contact, showAddress, walletAddress]);

    const utils = trpc.useContext();
    const { mutateAsync: createContact, isLoading: isCreatingContact } =
        trpc.contact.create.useMutation({
            onSettled: () => {
                utils.contact.list.invalidate();
            },
            onError: (error) => {
                console.error(error);
                notify.error(`Error creating contact: ${error.message}`);
            },
        });
    const { mutateAsync: updateContact, isLoading: isUpdatingContact } =
        trpc.contact.update.useMutation({
            onSettled: () => {
                utils.contact.list.invalidate();
            },
            onError: (error) => {
                console.error(error);
                notify.error(`Error editing contact: ${error.message}`);
            },
        });
    const isMutating = useMemo(
        () => isCreatingContact || isUpdatingContact,
        [isCreatingContact, isUpdatingContact],
    );

    const onSave = useCallback(
        async (userInput: string) => {
            await (contact
                ? updateContact({
                      walletAddress,
                      id: contact.id,
                      name: userInput,
                      chain: contact.chain,
                      contactTypes: contact.contactTypes || [],
                  })
                : createContact({
                      walletAddress,
                      name: userInput,
                      chain,
                  }));
        },
        [chain, contact, createContact, updateContact, walletAddress],
    );

    if (isContactError) {
        return (
            <ExclamationTriangleIcon
                className="size-5 text-primary-yellow"
                title="Something went wrong"
            />
        );
    }

    return (
        <div className="flex items-center gap-1">
            <div className="flex h-3 w-4 items-center justify-center">
                {showRequestIcon && (
                    <PopupCard
                        sideValue="top"
                        cursorType="cursor-pointer"
                        trigger={
                            <Image
                                src="/images/request-logo.svg"
                                alt="Request Finance Logo"
                                width="14"
                                height="12"
                            />
                        }
                    >
                        Contact imported from your Request Invoice
                    </PopupCard>
                )}
            </div>
            <NameEdit
                defaultValue={name}
                onSave={onSave}
                placeholder={placeholder}
                isLoading={isLoading || isMutating}
            />
        </div>
    );
};

export default Contact;
